import { useState } from "react";
import React from "react";
import { Scale, Timer, PoundSterling, FileText } from "lucide-react";
import ProductRulesTab from "./ProductRulesTab";
import ExpiringItemsTab from "./ExpiringItemsTab";
import ClaimsTab from "./ClaimsTab";
import FinanceTab from "@/components/Analytics/FinanceTab";

// ── SCREEN ────────────────────────────────────────────────────────────────────

export type TitanSubTab = "expiring-items" | "product-rules" | "finance" | "claims";

interface TitanScreenProps {
  isDarkMode: boolean;
  // Optional controlled sub-tab. When omitted the screen manages its own state.
  subTab?: TitanSubTab;
  onSubTabChange?: (tab: TitanSubTab) => void;
}

const subTabs: { id: TitanSubTab; label: string; icon: React.ReactNode }[] = [
  { id: "expiring-items",  label: "Expiring Items",  icon: <Timer className="w-4 h-4" /> },
  { id: "product-rules",   label: "Product Rules",   icon: <Scale className="w-4 h-4" /> },
  { id: "finance",         label: "Profit & Loss",   icon: <PoundSterling className="w-4 h-4" /> },
  { id: "claims",          label: "Claims",          icon: <FileText className="w-4 h-4" /> },
];

export default function TitanScreen({ isDarkMode, subTab, onSubTabChange }: TitanScreenProps) {
  const [selectedBranches, setSelectedBranches] = useState<string[]>(["kingswood"]);
  const [internalSubTab, setInternalSubTab] = useState<TitanSubTab>("product-rules");
  const activeSubTab = subTab ?? internalSubTab;
  const changeSubTab = (tab: TitanSubTab) => {
    if (onSubTabChange) onSubTabChange(tab);
    else setInternalSubTab(tab);
  };

  return (
    <div className={`flex flex-col h-full ${isDarkMode ? "bg-gray-950 text-white" : "bg-gray-50 text-gray-900"}`}>
      {/* Sub-tab bar */}
      <div className={`flex items-center gap-1 px-6 pt-4 pb-0 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}>
        {subTabs.map((tab) => {
          const isActive = activeSubTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => changeSubTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-all duration-200 border-b-2 -mb-px cursor-pointer ${
                isActive
                  ? isDarkMode ? "border-purple-500 text-white" : "border-gray-900 text-gray-900"
                  : isDarkMode ? "border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Sub-tab content */}
      <div className="flex-1 overflow-auto p-6">
        {activeSubTab === "expiring-items" && <ExpiringItemsTab isDarkMode={isDarkMode} selectedBranches={selectedBranches} onBranchChange={(id) => setSelectedBranches([id])} />}
        {activeSubTab === "product-rules" && <ProductRulesTab isDarkMode={isDarkMode} selectedBranches={selectedBranches} />}
        {activeSubTab === "claims" && <ClaimsTab isDarkMode={isDarkMode} selectedBranches={selectedBranches} onBranchChange={(id) => setSelectedBranches([id])} />}
        {activeSubTab === "finance" && (
          <div className="-m-6">
            <FinanceTab isDarkMode={isDarkMode} cardBgClass={isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white"} textClass={isDarkMode ? "text-gray-100" : "text-gray-900"} mutedTextClass={isDarkMode ? "text-gray-400" : "text-gray-600"} />
          </div>
        )}
      </div>
    </div>
  );
}
